import { db, auth } from "../../firebase";
import { firebaseConfig } from "../../firebase";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import {
  collection,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  onSnapshot,
  doc,
  setDoc,
  collectionGroup,
  query,
  where
} from "firebase/firestore";
import useStore from "../../store/useStore";

const addStaffMember = async (vendorUid, staffData) => {
  try {
    const locId = staffData.storeLocation || staffData.locationId;
    if (!locId) {
      throw new Error("Store location (locationId) is required to add staff.");
    }
    const staffCollection = collection(db, "vendorUsers", vendorUid, "branchStores", locId, "staff");
    
    if (staffData.role === "Manager") {
      // 1. Create a temporary secondary Firebase instance to prevent signing out the Vendor
      const secondaryApp = initializeApp(firebaseConfig, `Secondary_App_${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);
      
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, staffData.email, staffData.password);
      const newManagerUid = userCredential.user.uid;

      // 2. Remove plain text password before saving to Firestore
      const { password, ...safeData } = staffData;
      
      // 3. Save Manager strictly with their real Firebase Auth UID
      const managerDocRef = doc(db, "vendorUsers", vendorUid, "branchStores", locId, "staff", newManagerUid);
      await setDoc(managerDocRef, {
        ...safeData,
        vendorId: vendorUid,
        locationId: locId,
        createdAt: serverTimestamp(),
      });
      
      // Cleanup the secondary app instance locally (Note: Secondary Firebase instances persist in memory, so just let GC handle it or await secondaryApp.delete())
      return newManagerUid;
    } else {
      // Regular staff members skip Firebase Auth, simply adding normally with a pinCode
      const docRef = await addDoc(staffCollection, {
        ...staffData,
        vendorId: vendorUid,
        locationId: locId,
        createdAt: serverTimestamp(),
      });
      return docRef.id;
    }

  } catch (error) {
    console.error("Error adding staff:", error);
    throw error;
  }
};

// Get all staff members for a vendor across all locations
const listenToStaffMembers = (vendorUid, callback) => {
  try {
    const staffQuery = query(
      collectionGroup(db, "staff"),
      where("vendorId", "==", vendorUid)
    );

    const unsubscribe = onSnapshot(
      staffQuery,
      (snapshot) => {
        const staffList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(staffList);
      },
      (error) => {
        console.error("Error listening to staff:", error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up staff listener:", error);
    return () => {};
  }
};

// Get staff for a SPECIFIC branch only
const listenToBranchStaff = (vendorUid, locationId, callback) => {
  try {
    if (!locationId) return () => {};
    const staffCollection = collection(db, "vendorUsers", vendorUid, "branchStores", locationId, "staff");

    const unsubscribe = onSnapshot(
      staffCollection,
      (snapshot) => {
        const staffList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(staffList);
      },
      (error) => {
        console.error("Error listening to branch staff:", error);
      }
    );
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up branch staff listener:", error);
    return () => {};
  }
};

const listenToStaffCount = (setStaffCount) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.warn("userId is undefined, skipping staff count listener");
    return;
  }
  
  const staffQuery = query(
    collectionGroup(db, "staff"),
    where("vendorId", "==", userId)
  );

  const unsubscribe = onSnapshot(
    staffQuery,
    (snapshot) => {
      setStaffCount(snapshot.size);
    },
    (error) => {
      console.error("Error listening to staff count:", error);
    }
  );

  return unsubscribe;
};

// Update a staff member
const updateStaffMember = async (vendorUid, staffId, staffData) => {
  try {
    const locId = staffData.storeLocation || staffData.locationId;
    if (!locId) throw new Error("Missing locationId to update staff doc");

    const staffDoc = doc(db, "vendorUsers", vendorUid, "branchStores", locId, "staff", staffId);
    
    // Client-side mapping prevents easy password updates for Auth users without re-authenticating. Strip password here to prevent accidental plain-text storage.
    const { password, ...safeData } = staffData;
    
    await updateDoc(staffDoc, safeData);
  } catch (error) {
    console.error("Error updating staff:", error);
    throw error;
  }
};

const DeleteStaffMember = async (staffId, locationId) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.warn("userId is undefined, skipping staff delete");
    return;
  }
  if (!locationId) {
    throw new Error("Missing locationId to delete staff doc");
  }
  try {
    const staffDoc = doc(db, "vendorUsers", userId, "branchStores", locationId, "staff", staffId);
    await deleteDoc(staffDoc);
  } catch (error) {
    console.error("Error deleting staff:", error);
    throw error;
  }
};

export {
  addStaffMember,
  listenToStaffMembers,
  listenToBranchStaff,
  listenToStaffCount,
  updateStaffMember,
  DeleteStaffMember,
};
